import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TemplateOverviewResponse } from '../../api/templates';
import { VorlageActionType } from './vorlage-action-type.enum';
import { VorlageActionModel } from './vorlage-action.model';

@Component({
  selector: 'app-vorlagen-aktionen',
  templateUrl: './vorlagen-aktionen.component.html',
  styleUrls: ['./vorlagen-aktionen.component.scss'],
})
export class VorlagenAktionenComponent implements OnInit {

  @Input() template: TemplateOverviewResponse;
  @Input() iAmOwner: boolean;

  @Output() clickActionType = new EventEmitter<{ templateId: number; actionType: VorlageActionType }>();

  iconActions = [
    VorlageActionType.EDIT_TEMPLATE,
    VorlageActionType.SHOW_PREVIEW,
    VorlageActionType.EXPORT_TEMPLATE,
  ];
  contextMenuActions = [
    VorlageActionType.EDIT_TEMPLATE_NAME,
    VorlageActionType.DELETE_TEMPLATE,
    VorlageActionType.CREATE_NOTICE,
    VorlageActionType.COPY_TEMPLATE,
    VorlageActionType.CREATE_NOTICE_FROM_GROUPMEMBER,
    VorlageActionType.COPY_TEMPLATE_OF_GROUPMEMBER,
  ];

  iconVm: VorlageActionModel[] = [];
  contextMenuVm: VorlageActionModel[] = [];

  VorlageActionType = VorlageActionType;

  ngOnInit(): void {
    this.iconVm = this.createViewModels(this.iconActions);
    this.contextMenuVm = this.createViewModels(this.contextMenuActions);
  }

  onActionClick(menuItem: VorlageActionModel): void {
    this.clickActionType.emit({ templateId: this.template.templateId, actionType: menuItem.type });
  }

  private createViewModels(types: VorlageActionType[]): VorlageActionModel[] {
    return types.map(type => this.createViewModel(type));
  }

  private createViewModel(type: VorlageActionType): VorlageActionModel {
    const isEnabled = this.isEnabled(type);
    return {
      type,
      enabled: isEnabled,
      iconName: this.getIconNameByType(type),
      tooltip: this.getTooltipByTypeAndEnabled(type, isEnabled),
    };
  }

  private isEnabled(type: VorlageActionType): boolean {
    switch (type) {
      case VorlageActionType.SHOW_PREVIEW:
      case VorlageActionType.EXPORT_TEMPLATE:
        return true;
      case VorlageActionType.EDIT_TEMPLATE:
      case VorlageActionType.EDIT_TEMPLATE_NAME:
      case VorlageActionType.DELETE_TEMPLATE:
        return this.iAmOwner;
      case VorlageActionType.COPY_TEMPLATE:
        return this.iAmOwner && this.template.isLatestSDKVersion;
      case VorlageActionType.CREATE_NOTICE:
        return this.iAmOwner && this.template.isLatestSDKVersion;
      case VorlageActionType.COPY_TEMPLATE_OF_GROUPMEMBER:
        return !this.iAmOwner;
      case VorlageActionType.CREATE_NOTICE_FROM_GROUPMEMBER:
        return !this.iAmOwner && this.template.isLatestSDKVersion;
    }
  }

  private getIconNameByType(type: VorlageActionType): string {
    switch (type) {
      case VorlageActionType.EDIT_TEMPLATE:
        return 'edit';
      case VorlageActionType.SHOW_PREVIEW:
        return 'preview';
      case VorlageActionType.EXPORT_TEMPLATE:
        return 'download';
      case VorlageActionType.DELETE_TEMPLATE:
        return 'delete';
      case VorlageActionType.EDIT_TEMPLATE_NAME:
        return 'edit_note';
    }
  }

  private readonly ENABLED_TOOLTIPS = {
    EDIT_TEMPLATE: 'Vorlage bearbeiten',
    SHOW_PREVIEW: 'Vorschau anzeigen',
    EXPORT_TEMPLATE: 'Vorlage exportieren',
  };

  private readonly DISABLED_TOOLTIPS = {
    EDIT_TEMPLATE: 'Vorlage bearbeiten',
    SHOW_PREVIEW: 'Vorschau anzeigen',
    EXPORT_TEMPLATE: 'Vorlage exportieren',
    OWNER_ONLY: 'Diese Aktion ist nur für eigene Vorlagen möglich.',
    GROUP_MEMBER_ONLY: 'Diese Aktion ist nur für Vorlagen anderer Gruppenmitglieder möglich.',
    SDK_VERSION: 'Vorlagen können nur kopiert werden, wenn sie gemäß dem aktuellen eForms-Standard gespeichert sind. Um die Vorlage in den neuen Standard zu migrieren, klicken Sie auf "Vorlage bearbeiten".',
    NOTICE_SDK_VERSION: 'Bekanntmachungen können nur aus einer Vorlage angelegt werden, wenn die Vorlage gemäß dem aktuellen eForms-Standard gespeichert ist. Um die Vorlage in den neuen Standard zu migrieren, klicken Sie auf "Vorlage bearbeiten".',
    NOTICE_GROUP_SDK_VERSION: 'Bekanntmachungen können nur aus einer Vorlage angelegt werden, wenn die Vorlage gemäß dem aktuellen eForms-Standard gespeichert ist.',
  };

  private getTooltipByTypeAndEnabled(type: VorlageActionType, enabled: boolean): string | undefined {
    if (enabled) {
      return this.getEnabledTooltip(type);
    }
    return this.getDisabledTooltip(type);
  }

  private getEnabledTooltip(type: VorlageActionType): string | undefined {
    switch (type) {
      case VorlageActionType.EDIT_TEMPLATE:
        return this.ENABLED_TOOLTIPS.EDIT_TEMPLATE;
      case VorlageActionType.SHOW_PREVIEW:
        return this.ENABLED_TOOLTIPS.SHOW_PREVIEW;
      case VorlageActionType.EXPORT_TEMPLATE:
        return this.ENABLED_TOOLTIPS.EXPORT_TEMPLATE;
      default:
        return undefined;
    }
  }

  private getDisabledTooltip(type: VorlageActionType): string | undefined {
    switch (type) {
      case VorlageActionType.EDIT_TEMPLATE:
        return this.DISABLED_TOOLTIPS.EDIT_TEMPLATE;
      case VorlageActionType.SHOW_PREVIEW:
        return this.DISABLED_TOOLTIPS.SHOW_PREVIEW;
      case VorlageActionType.EXPORT_TEMPLATE:
        return this.DISABLED_TOOLTIPS.EXPORT_TEMPLATE;
      case VorlageActionType.EDIT_TEMPLATE_NAME:
      case VorlageActionType.DELETE_TEMPLATE:
        return this.DISABLED_TOOLTIPS.OWNER_ONLY;
      case VorlageActionType.COPY_TEMPLATE:
        return !this.iAmOwner ? this.DISABLED_TOOLTIPS.OWNER_ONLY : this.DISABLED_TOOLTIPS.SDK_VERSION;
      case VorlageActionType.CREATE_NOTICE:
        return !this.iAmOwner ? this.DISABLED_TOOLTIPS.OWNER_ONLY : this.DISABLED_TOOLTIPS.NOTICE_SDK_VERSION;
      case VorlageActionType.COPY_TEMPLATE_OF_GROUPMEMBER:
        return this.DISABLED_TOOLTIPS.GROUP_MEMBER_ONLY;
      case VorlageActionType.CREATE_NOTICE_FROM_GROUPMEMBER:
        return this.iAmOwner ? this.DISABLED_TOOLTIPS.GROUP_MEMBER_ONLY : this.DISABLED_TOOLTIPS.NOTICE_GROUP_SDK_VERSION;
      default:
        return undefined;
    }
  }
}
